import { useCallback, useState } from "react";
import type { ExternalLinkListItem } from "./externalLinks.api";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/**
 * State machine behind the four external-link dialogs.
 *
 * They form a small flow rather than four independent modals — Manage opens Create/Edit/Stats/Revoke,
 * and those return to Manage — so ownership of "which dialog, on which link" lives here instead of
 * being spread across the File Manager. `useFileManager` only calls `openCreate` / `openManage` and
 * renders `<ExternalLinksHost/>`.
 */

export type ExternalLinkDialog = "none" | "create" | "edit" | "manage" | "stats" | "revoke";

export type UseExternalLinksResult = {
  dialog: ExternalLinkDialog;
  target: ExternalLinkTarget | null;
  activeLink: ExternalLinkListItem | null;
  /** Bumped on every mutation; the Manage list refetches when it changes. */
  refreshToken: number;
  /** Bumped when links change so the grid can refresh its badge counts. */
  linksChangedToken: number;
  openCreate: (target: ExternalLinkTarget) => void;
  openManage: (target: ExternalLinkTarget) => void;
  openEdit: (link: ExternalLinkListItem) => void;
  openStats: (link: ExternalLinkListItem) => void;
  openRevoke: (link: ExternalLinkListItem) => void;
  closeAll: () => void;
  /** Called by a dialog after a successful mutation. */
  handleMutated: () => void;
  /** Return to Manage from a child dialog (Edit / Stats / Revoke). */
  backToManage: () => void;
};

export function useExternalLinks(): UseExternalLinksResult {
  const [dialog, setDialog] = useState<ExternalLinkDialog>("none");
  const [target, setTarget] = useState<ExternalLinkTarget | null>(null);
  const [activeLink, setActiveLink] = useState<ExternalLinkListItem | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [linksChangedToken, setLinksChangedToken] = useState(0);

  const openCreate = useCallback((next: ExternalLinkTarget) => {
    setTarget(next);
    setActiveLink(null);
    setDialog("create");
  }, []);

  const openManage = useCallback((next: ExternalLinkTarget) => {
    setTarget(next);
    setActiveLink(null);
    setDialog("manage");
  }, []);

  const openEdit = useCallback((link: ExternalLinkListItem) => {
    setActiveLink(link);
    setDialog("edit");
  }, []);

  const openStats = useCallback((link: ExternalLinkListItem) => {
    setActiveLink(link);
    setDialog("stats");
  }, []);

  const openRevoke = useCallback((link: ExternalLinkListItem) => {
    setActiveLink(link);
    setDialog("revoke");
  }, []);

  const closeAll = useCallback(() => {
    setDialog("none");
    setTarget(null);
    setActiveLink(null);
  }, []);

  const backToManage = useCallback(() => {
    setActiveLink(null);
    setDialog("manage");
  }, []);

  /**
   * Two separate counters on purpose. `refreshToken` only re-runs the Manage list, which is cheap;
   * `linksChangedToken` re-runs the grid's badge-count fetch, which touches every visible row — so
   * a dialog that merely reopens must not trigger it.
   */
  const handleMutated = useCallback(() => {
    setRefreshToken((v) => v + 1);
    setLinksChangedToken((v) => v + 1);
  }, []);

  return {
    dialog,
    target,
    activeLink,
    refreshToken,
    linksChangedToken,
    openCreate,
    openManage,
    openEdit,
    openStats,
    openRevoke,
    closeAll,
    handleMutated,
    backToManage,
  };
}
